// Lalive listing (collection + search results), design round 3.
// - Filters/sort apply at once; results, chips and counts swap in place via
//   the Section Rendering API. The open filter group is left untouched so
//   focus and the user's ticks survive; closed groups are replaced.
// - Chip / clear links ([data-lv-ajax]) swap everything, keeping which
//   groups were open.
// - "Daha fazla göster" appends the next page's cards; it also fires by
//   itself when it comes within 800px of the viewport (Samet, 10-05: load
//   while scrolling). The button stays as the fallback.
// - Mobile: the same form is a bottom sheet ([data-lv-sheet]).
// - Value search inside long groups; "Tümünü göster".
// Without JS the form submits and links navigate normally.
(() => {
  if (window.__lvListing) return;
  window.__lvListing = true;

  const mobile = () => window.matchMedia('(max-width: 749px)').matches;
  const root = (el) => el.closest('[data-lv-listing]');

  const fetchSection = async (url, sectionId) => {
    const u = new URL(url, location.origin);
    u.searchParams.set('section_id', sectionId);
    const res = await fetch(u.toString(), { headers: { Accept: 'text/html' } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    return doc.querySelector('[data-lv-listing]');
  };

  const formUrl = (form) => {
    const u = new URL(form.getAttribute('action'), location.origin);
    const sortDefault = form.querySelector('[data-lv-sort]')?.dataset.default;
    for (const [k, v] of new FormData(form)) {
      if (String(v).trim() === '') continue;
      if (k === 'sort_by' && v === sortDefault) continue; // keep shared URLs short
      u.searchParams.append(k, v);
    }
    return u;
  };

  const cleanUrl = (u) => {
    const x = new URL(u, location.origin);
    x.searchParams.delete('section_id');
    x.searchParams.delete('page');
    return x.pathname + (x.search ? x.search : '');
  };

  // Swap regions. keepOpen=true leaves open groups alone (form-driven change).
  const apply = (cur, next, keepOpen) => {
    cur.querySelectorAll('[data-lv-region]').forEach((el) => {
      const key = el.dataset.lvRegion;
      const fresh = next.querySelector(`[data-lv-region="${key}"]`);
      if (fresh) el.replaceWith(fresh);
      else if (key === 'results') el.innerHTML = '';
    });
    // Filter groups follow the server's list: a group the new results no
    // longer offer is removed (a stale "Beden" led to 0 results), a new one is
    // inserted in order. The group the shopper has open is not moved, so focus
    // stays on its checkbox (audit 10-05).
    const box = cur.querySelector('.lv-facets__groups');
    const freshBox = next.querySelector('.lv-facets__groups');
    if (box && freshBox) {
      const freshList = [...freshBox.querySelectorAll(':scope > [data-lv-group]')];
      const keys = new Set(freshList.map((f) => f.dataset.lvGroup));
      box.querySelectorAll(':scope > [data-lv-group]').forEach((g) => { if (!keys.has(g.dataset.lvGroup)) g.remove(); });
      let anchor = null;
      freshList.forEach((fresh) => {
        const g = box.querySelector(`:scope > [data-lv-group="${CSS.escape(fresh.dataset.lvGroup)}"]`);
        if (g && g.open && keepOpen) {
          // Only the badge/summary in the toggle follow the new state.
          const t = g.querySelector('summary'), ft = fresh.querySelector('summary');
          if (t && ft) t.innerHTML = ft.innerHTML;
          t?.classList.toggle('is-active', ft?.classList.contains('is-active'));
          const clearOld = g.querySelector('.lv-fgroup__foot'), clearNew = fresh.querySelector('.lv-fgroup__foot');
          if (clearOld && clearNew) clearOld.innerHTML = clearNew.innerHTML;
          anchor = g;
          return;
        }
        if (g) { fresh.open = g.open; g.replaceWith(fresh); }
        else if (anchor) anchor.after(fresh);
        else box.prepend(fresh);
        anchor = fresh;
      });
    }
    const label = next.querySelector('[data-lv-show-count]')?.textContent;
    if (label) cur.querySelectorAll('[data-lv-show-count]').forEach((b) => (b.textContent = label));
  };

  let seq = 0;
  const update = async (listing, url, keepOpen) => {
    const id = ++seq;
    listing.classList.add('is-loading');
    try {
      const next = await fetchSection(url, listing.dataset.section);
      if (id !== seq || !next) return; // a newer request won
      apply(listing, next, keepOpen);
      // Results refreshed behind an open sheet are new nodes: keep them inert too.
      const openSheetEl = listing.querySelector('[data-lv-sheet].is-open');
      if (openSheetEl) { clearInert(); setInert(openSheetEl); }
      history.replaceState(history.state, '', cleanUrl(url));
      watchMore();
    } catch (e) {
      location.href = cleanUrl(url); // fall back to a normal page load
    } finally {
      if (id === seq) listing.classList.remove('is-loading');
    }
  };

  let timer;
  document.addEventListener('change', (e) => {
    const form = e.target.closest('[data-lv-facets-form]');
    if (!form || !e.target.name) return;
    const listing = root(form);
    if (!listing) return;
    clearTimeout(timer);
    timer = setTimeout(() => update(listing, formUrl(form), true), e.target.dataset.lvPrice !== undefined ? 0 : 150);
  });

  // Enter in a price box applies instead of submitting the whole form.
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && e.target.matches('[data-lv-price]')) {
      e.preventDefault();
      e.target.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (e.key === 'Escape') {
      document.querySelectorAll('[data-lv-listing] details.lv-fgroup[open]').forEach((d) => {
        if (mobile()) return;
        const hadFocus = d.contains(document.activeElement);
        d.open = false;
        if (hadFocus) d.querySelector('summary')?.focus(); // not lost to <body> (audit 10-05)
      });
      closeSheet();
    }
  });

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-lv-facets-form]');
    const listing = form && root(form);
    if (!listing) return;
    e.preventDefault();
    update(listing, formUrl(form), true);
  });

  // Value search inside a group: filter, bold the match, count matches.
  const esc = (s) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
  document.addEventListener('input', (e) => {
    const box = e.target.closest('[data-lv-vsearch]');
    if (!box) return;
    const group = box.closest('[data-lv-group]');
    const values = group.querySelector('[data-lv-values]');
    const q = box.value.trim().toLocaleLowerCase('tr');
    let n = 0;
    values.classList.toggle('is-searching', q !== '');
    values.querySelectorAll('.lv-check').forEach((row) => {
      const span = row.querySelector('[data-lv-vlabel]');
      const text = span.dataset.text ?? (span.dataset.text = span.textContent);
      const i = q ? text.toLocaleLowerCase('tr').indexOf(q) : -1;
      const hit = !q || i >= 0;
      row.hidden = !hit;
      if (hit) n += 1;
      span.innerHTML = i >= 0 ? esc(text.slice(0, i)) + '<b>' + esc(text.slice(i, i + q.length)) + '</b>' + esc(text.slice(i + q.length)) : esc(text);
    });
    const meta = group.querySelector('[data-lv-vmatches]');
    if (meta) {
      meta.hidden = !q;
      meta.textContent = (meta.dataset.template || '').replace('[count]', n);
    }
  });

  document.addEventListener('click', (e) => {
    const t = e.target;

    const more = t.closest('[data-lv-vmore]');
    if (more) {
      more.closest('[data-lv-group]').querySelector('[data-lv-values]').classList.add('is-expanded');
      more.hidden = true;
      return;
    }

    const panelClose = t.closest('[data-lv-panel-close]');
    if (panelClose && !mobile()) {
      const d = panelClose.closest('details');
      if (d) {
        d.open = false;
        d.querySelector('summary')?.focus();
      }
      return;
    }

    if (t.closest('[data-lv-sheet-open]')) return openSheet(t.closest('[data-lv-listing]'));
    if (t.closest('[data-lv-sheet-close]')) return closeSheet();

    const ajax = t.closest('a[data-lv-ajax]');
    if (ajax) {
      const listing = root(ajax);
      if (!listing) return;
      e.preventDefault();
      return update(listing, ajax.href, false);
    }

    const loadMore = t.closest('a[data-lv-more]');
    if (loadMore) {
      const listing = root(loadMore);
      if (!listing) return;
      e.preventDefault();
      return appendPage(listing, loadMore);
    }

    // Desktop: one dropdown at a time; a click outside closes it.
    if (!mobile()) {
      const inGroup = t.closest('details.lv-fgroup');
      document.querySelectorAll('[data-lv-listing] details.lv-fgroup[open]').forEach((d) => {
        if (d !== inGroup) d.open = false;
      });
    }
  });

  // Opening a group by its summary closes the others (desktop).
  document.addEventListener(
    'toggle',
    (e) => {
      const d = e.target;
      if (!(d instanceof HTMLDetailsElement) || !d.matches('.lv-fgroup') || !d.open || mobile()) return;
      d.closest('[data-lv-listing]')
        .querySelectorAll('details.lv-fgroup[open]')
        .forEach((o) => o !== d && (o.open = false));
    },
    true
  );

  const appendPage = async (listing, link) => {
    if (link.getAttribute('aria-busy') === 'true') return;
    link.setAttribute('aria-busy', 'true');
    const token = seq;
    try {
      const next = await fetchSection(link.href, listing.dataset.section);
      // A filter or sort replaced the grid while this page was loading: its
      // cards and next link belong to the old results (audit 10-05).
      if (token !== seq || !link.isConnected) return;
      const grid = listing.querySelector('[data-lv-grid]');
      const freshGrid = next?.querySelector('[data-lv-grid]');
      if (!grid || !freshGrid) throw new Error('grid');
      const first = freshGrid.querySelector('.lv-listing__item');
      freshGrid.querySelectorAll(':scope > .lv-listing__item').forEach((li) => grid.append(li));
      const pag = listing.querySelector('[data-lv-region="pagination"]');
      const freshPag = next.querySelector('[data-lv-region="pagination"]');
      if (pag && freshPag) pag.replaceWith(freshPag);
      // Keyboard users who pressed the button continue from the first new card.
      if (link === document.activeElement) first?.querySelector('a')?.focus({ preventScroll: true });
      watchMore();
    } catch (e) {
      if (token === seq && link.isConnected) location.href = link.href;
    }
  };

  // Auto-load: observe the current "Daha fazla göster" link(s).
  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          const listing = root(en.target);
          if (listing) appendPage(listing, en.target);
        });
      }, { rootMargin: '0px 0px 800px 0px' })
    : null;
  const watchMore = () => {
    if (!io) return;
    io.disconnect();
    document.querySelectorAll('[data-lv-listing] a[data-lv-more]').forEach((a) => io.observe(a));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchMore);
  else watchMore();

  // Mobile bottom sheet. While open, the rest of the page is inert so Tab
  // stays inside the sheet (it covers the screen; audit 10-05).
  let lastFocus = null;
  let inerted = [];
  const setInert = (sheet) => {
    inerted = [];
    for (let el = sheet; el && el.parentElement && el !== document.body; el = el.parentElement) {
      for (const sib of el.parentElement.children) {
        if (sib === el || sib.inert || /^(SCRIPT|STYLE|TEMPLATE|LINK)$/.test(sib.tagName)) continue;
        sib.inert = true;
        inerted.push(sib);
      }
    }
  };
  const clearInert = () => {
    inerted.forEach((el) => (el.inert = false));
    inerted = [];
  };
  const openSheet = (listing) => {
    const sheet = listing?.querySelector('[data-lv-sheet]');
    if (!sheet) return;
    lastFocus = document.activeElement;
    sheet.classList.add('is-open');
    document.documentElement.classList.add('lv-sheet-lock');
    listing.querySelector('[data-lv-sheet-open]')?.setAttribute('aria-expanded', 'true');
    setInert(sheet);
    sheet.querySelector('[data-lv-sheet-close]')?.focus();
  };
  const closeSheet = () => {
    const sheet = document.querySelector('[data-lv-sheet].is-open');
    if (!sheet) return;
    sheet.classList.remove('is-open');
    document.documentElement.classList.remove('lv-sheet-lock');
    clearInert();
    sheet.closest('[data-lv-listing]')?.querySelector('[data-lv-sheet-open]')?.setAttribute('aria-expanded', 'false');
    lastFocus?.focus?.();
  };
  // Search page: articles and pages from predictive search (sections/lv-search-extra).
  const loadExtra = async () => {
    const box = document.querySelector('[data-lv-search-extra]:empty');
    if (!box || !box.dataset.url) return;
    try {
      const res = await fetch(box.dataset.url);
      if (!res.ok) return;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const extra = doc.querySelector('[data-lv-extra]');
      if (!extra) return;
      box.append(extra);
      for (const kind of ['articles', 'pages']) {
        const tab = document.querySelector(`[data-lv-extra-tab="${kind}"]`);
        if (tab && Number(extra.dataset[kind]) > 0) {
          tab.textContent = extra.dataset[`${kind}Label`];
          tab.hidden = false;
        }
      }
    } catch (e) {
      /* optional block; the product results stand on their own */
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', loadExtra);
  else loadExtra();

  // Tap on the dimmed backdrop closes the sheet.
  document.addEventListener('click', (e) => {
    if (e.target.matches('[data-lv-sheet].is-open')) closeSheet();
  });
})();
